/**
 * Build Hasura `_bool_exp` where clauses scoped to customer IDs.
 */

/**
 * Scope users to a set of customer IDs (from current user's usertocustomers).
 * Includes only dealer-tier roles by default.
 */
export function buildScopedUsersWhere(
  customerIds: number[],
  options?: {
    role?: string | null;
    showInactive?: boolean;
    search?: string;
    features?: string[];
  },
): Record<string, unknown> {
  const conditions: Record<string, unknown>[] = [];

  // Core scoping: user must be linked to one of the accessible customer IDs
  conditions.push({
    _or: [
      { users_usertocustomers: { customer_id: { _in: customerIds } } },
      { customer_id: { _in: customerIds } },
    ],
  });

  // Only dealer-tier roles
  conditions.push({ default_role: { _ilike: '%dealer%' } });

  // Role filter
  if (options?.role) {
    conditions.push({ default_role: { _eq: options.role } });
  }

  // Active filter (default: show only active)
  if (!options?.showInactive) {
    conditions.push({ active: { _eq: true } });
  }

  // Search
  if (options?.search) {
    const searchTerm = `%${options.search}%`;
    conditions.push({
      _or: [
        { display_name: { _ilike: searchTerm } },
        { email: { _ilike: searchTerm } },
      ],
    });
  }

  // Feature filters
  if (options?.features && options.features.length > 0) {
    for (const featureName of options.features) {
      conditions.push({
        users_usertofeatures: { feature: { name: { _eq: featureName } } },
      });
    }
  }

  return { _and: conditions };
}

/**
 * Scope drivers to a set of customer IDs.
 */
export function buildScopedDriversWhere(
  customerIds: number[],
  options?: { search?: string },
): Record<string, unknown> {
  const conditions: Record<string, unknown>[] = [];

  // Core scoping: driver's user must be linked to one of the accessible customer IDs
  conditions.push({
    user: {
      _or: [
        { users_usertocustomers: { customer_id: { _in: customerIds } } },
        { customer_id: { _in: customerIds } },
      ],
    },
  });

  // Search
  if (options?.search) {
    const searchTerm = `%${options.search}%`;
    conditions.push({
      user: {
        _or: [
          { display_name: { _ilike: searchTerm } },
          { email: { _ilike: searchTerm } },
          { phone: { _ilike: searchTerm } },
        ],
      },
    });
  }

  return { _and: conditions };
}
